import { useEffect, useReducer, useRef } from 'react';
import PackService from '../../api/packService';
import PlayerStatisticsService, { getClientVersions } from '../../api/playerStatisticsService';
import { Pack } from '../../types/pack';
import useAppContext from '../../hooks/useAppContext';
import toast from 'react-hot-toast';
import React from 'react';

type Action =
  | { type: 'fetch' }
  | { type: 'loaded'; value: Pack[] | null }
  | { type: 'clientVersionsLoaded'; value: string[] };

interface PackState {
  Records: Pack[];
  ClientVersions: string[];
  Loading: boolean;
}

const DefaultPackState = {
  Records: [],
  ClientVersions: [],
  Loading: false,
} as PackState;

const reducer = (state: PackState, action: Action): PackState => {
  switch (action.type) {
    case 'fetch':
      return { ...state, Loading: true };
    case 'loaded': {
      if (action.value === null) {
        return { ...state, Loading: false };
      }

      return { ...state, Records: [...action.value], Loading: false };
    }
    case 'clientVersionsLoaded':
      return { ...state, ClientVersions: action.value };
    default:
      return state;
  }
};

export const usePackReducer = () => {
  const [data, dispatch] = useReducer(reducer, DefaultPackState);
  const { Lang } = useAppContext();
  const loading = useRef<boolean>(false);

  const fetchData = React.useCallback(async () => {
    dispatch({ type: 'fetch' });
    const records = await PackService.getAll(Lang);
    dispatch({ type: 'loaded', value: records });
  }, [Lang]);

  const fetchClientVersions = React.useCallback(async () => {
    try {
      const stats = await PlayerStatisticsService.get();
      dispatch({ type: 'clientVersionsLoaded', value: getClientVersions(stats) });
    } catch {
      dispatch({ type: 'clientVersionsLoaded', value: [] });
    }
  }, []);

  useEffect(() => {
    if (loading.current) return;
    loading.current = true;
    fetchData();
    fetchClientVersions();
  }, [fetchData, fetchClientVersions]);

  const open = async () => {
    dispatch({ type: 'fetch' });
    const result = await PackService.open(Lang);
    if (result) {
      toast.success('version opened');
      await fetchData();
    } else {
      toast.error('opening failed');
    }
  };

  const release = async () => {
    dispatch({ type: 'fetch' });
    const result = await PackService.release(Lang);
    if (result) {
      toast.success('version released');
      await fetchData();
    } else {
      toast.error('release failed');
    }
  };

  const validate = async (pack: Pack) => {
    dispatch({ type: 'fetch' });
    const result = await PackService.validate(pack.lang, pack.version);
    if (result) {
      toast.success('version valid');
    } else {
      toast.error('version invalid');
    }
  };

  const updateVersionLimit = async (pack: Pack, versionLimit: string | null) => {
    const result = await PackService.updateVersionLimit(pack.lang, pack.version, versionLimit);
    if (result) {
      dispatch({
        type: 'loaded',
        value: data.Records.map((r) =>
          r.id === pack.id ? { ...r, versionLimit: result.versionLimit } : r
        ),
      });
    } else {
      toast.error('version limit update failed');
    }
  };

  const remove = async (pack: Pack) => {
    dispatch({ type: 'fetch' });
    const result = await PackService.delete(pack.id);
    if (result) {
      toast.success('pack deleted');
      await fetchData();
    } else {
      toast.error('pack delete failed');
      dispatch({ type: 'loaded', value: data.Records });
    }
  };

  return {
    data,
    open,
    release,
    validate,
    updateVersionLimit,
    remove,
  };
};
